const DEFAULT_SCHEDULER_URL = 'http://localhost:3100';

const hoursFromNow = (h) => new Date(Date.now() + h * 3600 * 1000).toISOString();
const atLocalHour = (dayOffset, hour, minute = 0) => {
  const d = new Date();
  d.setDate(d.getDate() + dayOffset);
  d.setHours(hour, minute, 0, 0);
  return d.toISOString();
};

/** Sample bookings for preview when scheduler is empty / unreachable. */
export const SAMPLE_DASHBOARD_BOOKINGS = [
  {
    id: 'sample-dash-today-1',
    start_time: atLocalHour(0, 11, 0),
    end_time: atLocalHour(0, 11, 30),
    duration_minutes: 30,
    consultant_name: 'Priya Sharma',
    consultant_state: 'Texas',
    vendor_name: 'Apex Health Staffing',
    client_name: 'Northstar Medical',
    diologic_coordinator: 'Alex Rivera',
    call_type: 'Video',
    status: 'Confirmed',
    notes: 'Zoom link shared 30 minutes before.',
  },
  {
    id: 'sample-dash-today-2',
    start_time: atLocalHour(0, 15, 30),
    end_time: atLocalHour(0, 16, 0),
    duration_minutes: 30,
    consultant_name: 'Jordan Lee',
    consultant_state: 'California',
    vendor_name: 'MedBridge Partners',
    client_name: 'Summit Care Group',
    diologic_coordinator: 'Alex Rivera',
    call_type: 'Audio',
    status: 'Confirmed',
    notes: '',
  },
  {
    id: 'sample-dash-up-1',
    start_time: hoursFromNow(26),
    end_time: hoursFromNow(26.5),
    duration_minutes: 30,
    consultant_name: 'Priya Sharma',
    consultant_state: 'Texas',
    vendor_name: 'Apex Health Staffing',
    client_name: 'Lakeside Clinic',
    diologic_coordinator: 'Jordan Lee',
    call_type: 'Video',
    status: 'Confirmed',
    notes: '',
  },
  {
    id: 'sample-dash-up-2',
    start_time: hoursFromNow(50),
    end_time: hoursFromNow(51),
    duration_minutes: 60,
    consultant_name: 'Sam Patel',
    consultant_state: 'New York',
    vendor_name: 'CareLink Staffing',
    client_name: 'Horizon Health',
    diologic_coordinator: 'Alex Rivera',
    call_type: 'Video',
    status: 'Confirmed',
    notes: 'Panel interview — prepare ICD-10 examples.',
  },
];

export function getSchedulerBaseUrl() {
  return (process.env.REACT_APP_SCHEDULER_URL || DEFAULT_SCHEDULER_URL).replace(/\/$/, '');
}

export function isSchedulerDemoMode() {
  try {
    const param = new URLSearchParams(window.location.search).get('demo');
    if (param === '1') window.sessionStorage.setItem('demoMode', '1');
    else if (param === '0' || param === 'off') window.sessionStorage.removeItem('demoMode');
    return window.sessionStorage.getItem('demoMode') === '1';
  } catch {
    return false;
  }
}

export function formatBookingWhen(isoStart, isoEnd, durationMinutes) {
  if (!isoStart) return { date: '—', time: '—', range: '—' };
  const start = new Date(isoStart);
  const end = isoEnd
    ? new Date(isoEnd)
    : new Date(start.getTime() + (durationMinutes || 30) * 60000);
  const date = start.toLocaleDateString(undefined, {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
  });
  const opts = { hour: 'numeric', minute: '2-digit' };
  const time = `${start.toLocaleTimeString(undefined, opts)} – ${end.toLocaleTimeString(undefined, opts)}`;
  return { date, time, range: `${date} · ${time}` };
}

function isSameLocalDay(iso, ref = new Date()) {
  const d = new Date(iso);
  if (!Number.isFinite(d.getTime())) return false;
  return (
    d.getFullYear() === ref.getFullYear() &&
    d.getMonth() === ref.getMonth() &&
    d.getDate() === ref.getDate()
  );
}

/** Split into today (local calendar day) and upcoming (after today). */
export function splitTodayAndUpcoming(bookings) {
  const now = new Date();
  const today = [];
  const upcoming = [];
  (bookings || []).forEach((b) => {
    const t = new Date(b.start_time).getTime();
    if (!Number.isFinite(t)) return;
    if (isSameLocalDay(b.start_time, now)) today.push(b);
    else if (t >= now.getTime() - 30 * 60 * 1000) upcoming.push(b);
  });
  today.sort((a, b) => new Date(a.start_time) - new Date(b.start_time));
  upcoming.sort((a, b) => new Date(a.start_time) - new Date(b.start_time));
  return { today, upcoming };
}

export async function fetchAdminBookings() {
  const base = getSchedulerBaseUrl();
  const from = new Date();
  from.setHours(0, 0, 0, 0);
  const r = await fetch(`${base}/api/bookings?from=${encodeURIComponent(from.toISOString())}`);
  const data = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(data.error || 'Failed to load bookings');
  return Array.isArray(data.bookings) ? data.bookings : [];
}

export async function fetchConsultantBookings({ email, name } = {}) {
  const base = getSchedulerBaseUrl();
  const qs = new URLSearchParams();
  if (email) qs.set('email', email);
  if (name) qs.set('name', name);
  const r = await fetch(`${base}/api/consultant-bookings?${qs}`);
  const data = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(data.error || 'Failed to load bookings');
  return Array.isArray(data.bookings) ? data.bookings : [];
}
